import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mantine/core';
import styles from './TextTemplates.module.css';

interface TextTemplate {
  id: string;
  name: string;
  text: string;
  style: {
    fontSize: number;
    fontFamily: string;
    color: string;
    textAlign?: 'left' | 'center' | 'right';
    shadow?: {
      enabled: boolean;
      color: string;
      offsetX: number;
      offsetY: number;
      blur: number;
    };
    border?: {
      enabled: boolean;
      size: number;
      color: string;
    };
  };
}

interface TextTemplatesProps {
  onApplyTemplate: (template: TextTemplate) => void;
}

const predefinedTemplates: TextTemplate[] = [
  {
    id: 'epic-title',
    name: 'Epic Title',
    text: 'EPIC CONTENT',
    style: {
      fontSize: 64,
      fontFamily: 'Bebas Neue',
      color: '#FFFFFF',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 3,
        offsetY: 3,
        blur: 6
      }
    }
  },
  {
    id: 'gaming-style',
    name: 'Gaming Style',
    text: 'LEVEL UP!',
    style: {
      fontSize: 56,
      fontFamily: 'Bangers',
      color: '#00FF00',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#003300',
        offsetX: 2,
        offsetY: 2,
        blur: 4
      },
      border: {
        enabled: true,
        size: 2,
        color: '#FFFFFF'
      }
    }
  },
  {
    id: 'modern-clean',
    name: 'Modern Clean',
    text: 'Your Title Here',
    style: {
      fontSize: 48,
      fontFamily: 'Anton',
      color: '#3B82F6',
      textAlign: 'center',
      shadow: {
        enabled: false,
        color: '#000000',
        offsetX: 0,
        offsetY: 0,
        blur: 0
      }
    }
  },
  {
    id: 'bold-impact',
    name: 'Bold Impact',
    text: 'WATCH NOW',
    style: {
      fontSize: 72,
      fontFamily: 'Alfa Slab One',
      color: '#FF0000',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 4,
        offsetY: 4,
        blur: 8
      },
      border: {
        enabled: true,
        size: 3,
        color: '#FFFFFF'
      }
    }
  },
  {
    id: 'premium-gold',
    name: 'Premium Gold',
    text: 'PREMIUM',
    style: {
      fontSize: 52,
      fontFamily: 'Titan One',
      color: '#FFD700',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#8B4513',
        offsetX: 2,
        offsetY: 2,
        blur: 4
      },
      border: {
        enabled: true,
        size: 2,
        color: '#8B4513'
      }
    }
  },
  {
    id: 'fun-playful',
    name: 'Fun & Playful',
    text: 'AMAZING!',
    style: {
      fontSize: 60,
      fontFamily: 'Luckiest Guy',
      color: '#FF6B6B',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#FFFFFF',
        offsetX: 2,
        offsetY: 2,
        blur: 4
      }
    }
  },
  // NEW POPULAR TEMPLATES
  {
    id: 'clickbait-red',
    name: 'Clickbait Red',
    text: 'YOU WON\'T BELIEVE',
    style: {
      fontSize: 58,
      fontFamily: 'Bebas Neue',
      color: '#FF0000',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 3,
        offsetY: 3,
        blur: 6
      },
      border: {
        enabled: true,
        size: 4,
        color: '#FFFFFF'
      }
    }
  },
  {
    id: 'neon-glow',
    name: 'Neon Glow',
    text: 'TRENDING NOW',
    style: {
      fontSize: 54,
      fontFamily: 'Orbitron',
      color: '#00FFFF',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#00FFFF',
        offsetX: 0,
        offsetY: 0,
        blur: 15
      }
    }
  },
  {
    id: 'fire-hot',
    name: 'Fire Hot',
    text: '🔥 VIRAL 🔥',
    style: {
      fontSize: 48,
      fontFamily: 'Bangers',
      color: '#FF4500',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#8B0000',
        offsetX: 2,
        offsetY: 2,
        blur: 4
      },
      border: {
        enabled: true,
        size: 2,
        color: '#FFD700'
      }
    }
  },
  {
    id: 'money-green',
    name: 'Money Green',
    text: '$10,000',
    style: {
      fontSize: 68,
      fontFamily: 'Anton',
      color: '#00FF00',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 4,
        offsetY: 4,
        blur: 8
      },
      border: {
        enabled: true,
        size: 3,
        color: '#FFFFFF'
      }
    }
  },
  {
    id: 'horror-scary',
    name: 'Horror Scary',
    text: 'HAUNTED',
    style: {
      fontSize: 56,
      fontFamily: 'Creepster',
      color: '#8B0000',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 5,
        offsetY: 5,
        blur: 10
      }
    }
  },
  {
    id: 'tech-cyber',
    name: 'Tech Cyber',
    text: 'AI POWERED',
    style: {
      fontSize: 50,
      fontFamily: 'Orbitron',
      color: '#8A2BE2',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#8A2BE2',
        offsetX: 0,
        offsetY: 0,
        blur: 12
      },
      border: {
        enabled: true,
        size: 2,
        color: '#FFFFFF'
      }
    }
  },
  {
    id: 'sports-action',
    name: 'Sports Action',
    text: 'LEGENDARY',
    style: {
      fontSize: 62,
      fontFamily: 'Alfa Slab One',
      color: '#1E90FF',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000080',
        offsetX: 3,
        offsetY: 3,
        blur: 6
      },
      border: {
        enabled: true,
        size: 3,
        color: '#FFFFFF'
      }
    }
  },
  {
    id: 'minimal-white',
    name: 'Minimal White',
    text: 'SIMPLE',
    style: {
      fontSize: 52,
      fontFamily: 'Roboto',
      color: '#FFFFFF',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 2,
        offsetY: 2,
        blur: 4
      }
    }
  },
  {
    id: 'retro-80s',
    name: 'Retro 80s',
    text: 'RADICAL',
    style: {
      fontSize: 58,
      fontFamily: 'Bungee',
      color: '#FF1493',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#00FFFF',
        offsetX: 4,
        offsetY: 4,
        blur: 0
      },
      border: {
        enabled: true,
        size: 2,
        color: '#FFFF00'
      }
    }
  },
  {
    id: 'question-mark',
    name: 'Question Mark',
    text: 'WHAT IF?',
    style: {
      fontSize: 55,
      fontFamily: 'Anton',
      color: '#FFA500',
      textAlign: 'center',
      shadow: {
        enabled: true,
        color: '#000000',
        offsetX: 3,
        offsetY: 3,
        blur: 6
      },
      border: {
        enabled: true,
        size: 2,
        color: '#FFFFFF'
      }
    }
  }
];

const TextTemplates: React.FC<TextTemplatesProps> = ({ onApplyTemplate }) => {
  const { t } = useTranslation('thumbnailEditor');
  
  const getTemplateName = (templateId: string) => {
    const nameMap: Record<string, string> = {
      'epic-title': t('thumbnailEditor.textTab.templateNames.epicTitle'),
      'gaming-style': t('thumbnailEditor.textTab.templateNames.gamingStyle'),
      'modern-clean': t('thumbnailEditor.textTab.templateNames.modernMinimal'),
      'bold-impact': t('thumbnailEditor.textTab.templateNames.bold'),
      'elegant-script': t('thumbnailEditor.textTab.templateNames.elegant'),
      'premium-gold': t('thumbnailEditor.textTab.templateNames.premiumGold'),
      'fun-playful': t('thumbnailEditor.textTab.templateNames.funPlayful'),
      'clickbait-red': t('thumbnailEditor.textTab.templateNames.clickbaitRed'),
      'neon-glow': t('thumbnailEditor.textTab.templateNames.neonGlow'),
      'fire-hot': t('thumbnailEditor.textTab.templateNames.fireHot'),
      'money-green': t('thumbnailEditor.textTab.templateNames.moneyGreen'),
      'horror-scary': t('thumbnailEditor.textTab.templateNames.horrorScary'),
      'tech-cyber': t('thumbnailEditor.textTab.templateNames.techCyber'),
      'sports-action': t('thumbnailEditor.textTab.templateNames.sportsAction'),
      'minimal-white': t('thumbnailEditor.textTab.templateNames.minimalWhite'),
      'retro-80s': t('thumbnailEditor.textTab.templateNames.retro80s'),
      'question-mark': t('thumbnailEditor.textTab.templateNames.questionMark')
    };
    return nameMap[templateId] || templateId;
  };
  
  const getTemplateText = (templateId: string) => {
    const textMap: Record<string, string> = {
      'epic-title': t('thumbnailEditor.textTab.templateText.epicTitle'),
      'gaming-style': t('thumbnailEditor.textTab.templateText.gamingStyle'),
      'modern-clean': t('thumbnailEditor.textTab.templateText.modernMinimal'),
      'bold-impact': t('thumbnailEditor.textTab.templateText.bold'),
      'elegant-script': t('thumbnailEditor.textTab.templateText.elegant'),
      'premium-gold': t('thumbnailEditor.textTab.templateText.premiumGold'),
      'fun-playful': t('thumbnailEditor.textTab.templateText.funPlayful'),
      'clickbait-red': t('thumbnailEditor.textTab.templateText.clickbaitRed'),
      'neon-glow': t('thumbnailEditor.textTab.templateText.neonGlow'),
      'fire-hot': t('thumbnailEditor.textTab.templateText.fireHot'),
      'money-green': t('thumbnailEditor.textTab.templateText.moneyGreen'),
      'horror-scary': t('thumbnailEditor.textTab.templateText.horrorScary'),
      'tech-cyber': t('thumbnailEditor.textTab.templateText.techCyber'),
      'sports-action': t('thumbnailEditor.textTab.templateText.sportsAction'),
      'minimal-white': t('thumbnailEditor.textTab.templateText.minimalWhite'),
      'retro-80s': t('thumbnailEditor.textTab.templateText.retro80s'),
      'question-mark': t('thumbnailEditor.textTab.templateText.questionMark')
    };
    return textMap[templateId] || templateId;
  };
  
  return (
    <div className={styles.templatesContainer}>
      <h4>{t('thumbnailEditor.textTab.templates')}</h4>
      <div className={styles.templateGrid}>
        {predefinedTemplates.map(template => (
          <div key={template.id} className={styles.templateCard}>
            <div 
              className={styles.templatePreview}
              style={{
                fontSize: `${Math.min(template.style.fontSize / 3, 20)}px`,
                fontFamily: template.style.fontFamily,
                color: template.style.color,
                textShadow: template.style.shadow?.enabled ? 
                  `${template.style.shadow.offsetX}px ${template.style.shadow.offsetY}px ${template.style.shadow.blur}px ${template.style.shadow.color}` : 
                  'none',
                WebkitTextStroke: template.style.border?.enabled ? 
                  `${Math.max(1, template.style.border.size / 2)}px ${template.style.border.color}` : 
                  'none'
              }}
            >
              {getTemplateText(template.id)}
            </div>
            <div className={styles.templateInfo}>
              <span className={styles.templateName}>{getTemplateName(template.id)}</span>
              <Button
                size="xs"
                onClick={() => onApplyTemplate({
                  ...template,
                  text: getTemplateText(template.id)
                })}
                className={styles.applyButton}
              >
                {t('thumbnailEditor.textTab.apply')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TextTemplates;